import { createFileRoute } from "@tanstack/react-router";
import { MicIcon, RotateCcwIcon, SendIcon, SquareIcon } from "lucide-react";
import { useCallback } from "react";

import { MarkdownContent } from "@/components/studyos/primitive-content/markdown-content";
import { cn } from "@/components/ui/lib/utils";
import { useAudioAnalysis } from "@/hooks/use-audio-analysis";

export const Route = createFileRoute("/preview/pronunciation")({
	component: PronunciationTest,
});

function PronunciationTest() {
	const referenceText = "The quick brown fox jumps over the lazy dog.";

	const {
		duration,
		audioBlob,
		startRecording,
		stopRecording,
		isRecording,
		isUploading,
		isAnalyzing,
		hasResult,
		analysisText,
		analysisError,
		analyze,
		reset,
	} = useAudioAnalysis({ referenceText, maxDuration: 30 });

	const handleStartRecording = useCallback(async () => {
		try {
			await startRecording();
		} catch {
			// mic access denied
		}
	}, [startRecording]);

	const handleAnalyze = useCallback(() => {
		if (audioBlob) {
			analyze(audioBlob);
		}
	}, [analyze, audioBlob]);

	const hasAudio = !!audioBlob || hasResult || isAnalyzing;

	return (
		<div className="flex min-h-screen items-center justify-center bg-background p-4">
			<div className="flex w-full max-w-lg flex-col gap-4 rounded-xl border bg-card p-6">
				{/* Header */}
				<div className="flex flex-col gap-1">
					<h3 className="text-lg font-semibold">Pronunciation Analysis</h3>
					<p className="text-sm text-muted-foreground">
						Record your speech and get AI-powered pronunciation feedback
					</p>
				</div>

				{/* Reference text */}
				<div className="rounded-lg bg-muted/50 p-3">
					<p className="text-xs font-medium text-muted-foreground">Reference Text</p>
					<p className="mt-1 text-sm font-medium">{referenceText}</p>
				</div>

				{/* Recording controls */}
				<div className="flex items-center gap-3">
					{!isRecording ? (
						<button
							type="button"
							onClick={handleStartRecording}
							disabled={isAnalyzing || isUploading}
							className={cn(
								"flex size-14 items-center justify-center rounded-full transition-colors",
								isAnalyzing || isUploading
									? "cursor-not-allowed bg-muted text-muted-foreground"
									: "bg-red-500 text-white hover:bg-red-600",
							)}
						>
							<MicIcon className="size-6" />
						</button>
					) : (
						<button
							type="button"
							onClick={stopRecording}
							className="flex size-14 animate-pulse items-center justify-center rounded-full bg-red-500 text-white"
						>
							<SquareIcon className="size-5" />
						</button>
					)}

					<div className="flex flex-col">
						{isRecording && (
							<>
								<span className="text-sm font-medium text-red-500">
									Recording...
								</span>
								<span className="text-xs text-muted-foreground">
									{duration.toFixed(1)}s / 30s
								</span>
							</>
						)}
						{!isRecording && !hasAudio && !analysisError && (
							<span className="text-sm text-muted-foreground">
								Tap the mic to start recording
							</span>
						)}
						{!isRecording && hasAudio && !isAnalyzing && !hasResult && (
							<span className="text-sm text-muted-foreground">
								Recording complete — {duration.toFixed(1)}s
							</span>
						)}
						{isUploading && (
							<span className="text-sm font-medium text-blue-500">Uploading...</span>
						)}
						{isAnalyzing && (
							<span className="text-sm font-medium text-blue-500">Analyzing...</span>
						)}
						{hasResult && (
							<span className="text-sm font-medium text-green-600">
								Analysis complete
							</span>
						)}
						{analysisError && (
							<span className="text-sm font-medium text-red-500">
								{analysisError.message || "Analysis failed"}
							</span>
						)}
					</div>
				</div>

				{/* Action buttons */}
				{(hasAudio || hasResult || analysisError) && (
					<div className="flex gap-2">
						{!isRecording && hasAudio && !isAnalyzing && !hasResult && (
							<button
								type="button"
								onClick={handleAnalyze}
								className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
							>
								<SendIcon className="size-3.5" />
								Analyze
							</button>
						)}
						<button
							type="button"
							onClick={reset}
							disabled={isAnalyzing || isUploading}
							className="flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
						>
							<RotateCcwIcon className="size-3.5" />
							Reset
						</button>
					</div>
				)}

				{/* Analyzing skeleton */}
				{isAnalyzing && !analysisText && (
					<div className="space-y-3 rounded-lg border p-4">
						<div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
						<div className="h-4 w-full animate-pulse rounded bg-muted" />
						<div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
						<div className="h-4 w-full animate-pulse rounded bg-muted" />
						<div className="pt-1" />
						<div className="h-5 w-1/2 animate-pulse rounded bg-muted" />
						<div className="h-4 w-full animate-pulse rounded bg-muted" />
						<div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
					</div>
				)}

				{/* Analysis result */}
				{analysisText && (
					<div className="max-h-[400px] overflow-y-auto rounded-lg border p-4">
						<MarkdownContent markdown={analysisText} size="compact" />
					</div>
				)}
			</div>
		</div>
	);
}
